/**
 * Responsive check
 * @param {Object} e
 * @returns {boolean}
 */
export function responsive_check(e) {
  let { mobile, tablet, desktop } = e.responsive.device;
  let display = false;
  display = window.innerWidth <= 640 && mobile ? true : display;
  display = window.innerWidth > 640 && window.innerWidth <= 1024 && tablet ? true : display;
  display = window.innerWidth > 1024 && desktop ? true : display;
  return display;
}

/**
 * Detects hotkey combinations
 * @param {string} combo - The hotkey combination (e.g., "ctrl+c", "alt+shift+s")
 * @param {Function} callback - Function to execute when combo is pressed
 * @param {Object} options - Configuration options
 * @param {HTMLElement} options.target - Element to listen on (default: document)
 * @param {boolean} options.preventDefault - Whether to prevent default behavior
 * @returns {Function} Cleanup function to remove listener
 */
export function detect_hotkey(combo, callback, options = {}) {
  const { target = document, preventDefault = true } = options;

  // Parse combo into keys (case-insensitive)
  const keys = combo.toLowerCase().split('+');
  const pressedKeys = new Set();

  // Key code map for special keys
  const keyMap = {
    ctrl: 'control',
    cmd: 'meta',
    command: 'meta',
    esc: 'escape',
    del: 'delete',
    up: 'arrowup',
    down: 'arrowdown',
    left: 'arrowleft',
    right: 'arrowright',
  };

  function handleKeyDown(e) {
    const key = e.key.toLowerCase();
    const normalizedKey = keyMap[key] || key;

    // Track pressed keys
    pressedKeys.add(normalizedKey);

    // Check if all keys in combo are pressed
    const allPressed = keys.every((k) => {
      const normalizedComboKey = keyMap[k] || k;
      return (
        pressedKeys.has(normalizedComboKey) ||
        (normalizedComboKey === 'control' && e.ctrlKey) ||
        (normalizedComboKey === 'shift' && e.shiftKey) ||
        (normalizedComboKey === 'alt' && e.altKey) ||
        (normalizedComboKey === 'meta' && e.metaKey)
      );
    });

    if (allPressed) {
      if (preventDefault) {
        e.preventDefault();
        e.stopPropagation();
      }
      callback(e);
    }
  }

  function handleKeyUp(e) {
    const key = e.key.toLowerCase();
    const normalizedKey = keyMap[key] || key;
    pressedKeys.delete(normalizedKey);
  }

  // Add event listeners
  target.addEventListener('keydown', handleKeyDown);
  target.addEventListener('keyup', handleKeyUp);

  // Return cleanup function
  return () => {
    target.removeEventListener('keydown', handleKeyDown);
    target.removeEventListener('keyup', handleKeyUp);
  };
}

/**
 * Detects when a user intends to leave the page (exit intent)
 * @param {Function} callback - Function to execute on exit intent
 * @param {Object} options - Configuration options
 * @param {number} options.delay - Delay before binding (ms)
 * @param {number} options.sensitivity - Mouse distance from top threshold (px)
 * @param {number} options.maxEvents - Maximum triggers per session
 */
export function detectExitIntent(callback, options = {}) {
  const config = {
    delay: 1000,
    sensitivity: 20,
    maxEvents: 1,
    ...options,
  };

  let triggeredCount = 0;
  let delayedHandler;

  // Delay initial binding to prevent immediate triggers
  delayedHandler = setTimeout(() => {
    document.addEventListener('mousemove', trackMouseMovement);
  }, config.delay);

  function trackMouseMovement(e) {
    // Detect when mouse leaves top of viewport
    if (e.clientY < config.sensitivity && triggeredCount < config.maxEvents) {
      triggeredCount++;
      callback();
      cleanup();
    }
  }

  function cleanup() {
    clearTimeout(delayedHandler);
    document.removeEventListener('mousemove', trackMouseMovement);
  }

  // Return cleanup function for manual control
  return cleanup;
}

/**
 * Detects if an ad blocker is active
 * @returns {Promise<boolean>} - True if ad blocker is active, false otherwise
 */
export function detectAdBlock() {
  return new Promise((resolve, reject) => {
    // Create a test element that ad blockers might target
    const test = document.createElement('div');
    test.innerHTML = '&nbsp;';
    test.className = 'adsbox adbox ad-box ads-box';
    document.body.appendChild(test);

    // Use a timeout to check if the element has been hidden or removed
    window.setTimeout(() => {
      const isAdBlocked =
        test.offsetHeight === 0 ||
        getComputedStyle(test).display === 'none' ||
        getComputedStyle(test).visibility === 'hidden';

      // Clean up the test element
      if (test.parentNode) {
        document.body.removeChild(test);
      }

      resolve(isAdBlocked);
    }, 100); // Adjust timeout as needed
  });
}

/**
 * Scroll to an element
 * @param {Object} element - The element to scroll to
 * @param {number} to - The position to scroll to
 * @param {number} duration - The duration of the scroll
 */
export function scrollTo(element, to, duration) {
  let start = element.scrollTop,
    change = to - start,
    currentTime = 0,
    increment = 20;

  let easeInOutQuad = function (t, b, c, d) {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  };

  let animateScroll = function () {
    currentTime += increment;
    const val = easeInOutQuad(currentTime, start, change, duration);
    element.scrollTop = val;
    if (currentTime < duration) {
      setTimeout(animateScroll, increment);
    }
  };
  animateScroll();
}
